import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';

// C-7: 응모자 userId → 마스킹 신원(이름/이메일) batch 조회.
// be-user: POST /api/admin/v1/users/identities {userIds:[]} → [{userId, maskedName, maskedEmail}]
// 미존재 userId 는 응답에서 생략 → 호출부는 '—' 폴백 (raw UUID 절대 미노출).

export interface UserIdentity {
  userId: string;
  maskedName: string;
  maskedEmail: string;
  /** user #39(QA-398) — 로그인 ID(가리지 않음). 로그인 ID 없이 가입한 회원·구버전 응답은 null/없음. */
  loginId?: string | null;
}

export type UserIdentityMap = ReadonlyMap<string, UserIdentity>;

const IDENTITIES_PATH = '/api/admin/v1/users/identities';

const fetchIdentities = async (userIds: ReadonlyArray<string>): Promise<UserIdentity[]> => {
  if (userIds.length === 0) return [];
  const res = await adminFetch(IDENTITIES_PATH, {
    method: 'POST',
    body: JSON.stringify({ userIds }),
  });
  if (!res.ok) throw new Error(`user identities fetch failed: ${res.status}`);
  const env = (await res.json()) as { data?: UserIdentity[] } | UserIdentity[];
  // 응답이 envelope({data}) 또는 raw array 어느 쪽이든 수용.
  const list = Array.isArray(env) ? env : (env.data ?? []);
  return Array.isArray(list) ? list : [];
};

export const userIdentitiesQueryKey = (userIds: ReadonlyArray<string>) =>
  ['admin', 'user-identities', [...userIds].sort()] as const;

/**
 * 중복 제거한 userId 집합을 batch 조회해 userId→identity Map 으로 반환.
 * userIds 가 비면 쿼리 비활성 + 빈 Map.
 */
export const useUserIdentitiesQuery = (userIds: ReadonlyArray<string>) => {
  const unique = Array.from(new Set(userIds.filter(Boolean)));
  return useQuery<UserIdentityMap>({
    queryKey: userIdentitiesQueryKey(unique),
    queryFn: async () => {
      const list = await fetchIdentities(unique);
      return new Map(list.map((i) => [i.userId, i]));
    },
    enabled: unique.length > 0,
    staleTime: 60_000,
  });
};
